import React, { useState, useCallback } from "react";
import { Button, Modal, ModalHeader, ModalBody } from "reactstrap";
import { useSyncStatus } from "./hooks/useSyncStatus";
import { formatDateTimeShort } from "../../../utils/date";

const badgeStyle = (synced) => ({
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: "12px",
  fontSize: "11px",
  fontWeight: 600,
  background: synced ? "#dcfce7" : "#fef2f2",
  color: synced ? "#166534" : "#991b1b",
});

const thStyle = {
  padding: "10px 12px",
  textAlign: "center",
  fontWeight: 600,
  fontSize: "13px",
  color: "#374151",
  borderBottom: "2px solid #e5e7eb",
  background: "#f9fafb",
};

const tdStyle = {
  padding: "10px 12px",
  textAlign: "center",
  fontSize: "13px",
  borderBottom: "1px solid #f3f4f6",
};

const pageBtnStyle = (active) => ({
  padding: "4px 10px",
  border: "1px solid #d1d5db",
  borderRadius: "4px",
  background: active ? "#3b82f6" : "#fff",
  color: active ? "#fff" : "#374151",
  cursor: "pointer",
  fontSize: "13px",
  fontWeight: active ? 600 : 400,
  minWidth: "32px",
});

const filterBtnStyle = (active) => ({
  padding: "4px 12px",
  borderRadius: "4px",
  border: active ? "1px solid #3b82f6" : "1px solid #d1d5db",
  background: active ? "#eff6ff" : "#fff",
  color: active ? "#1d4ed8" : "#6b7280",
  cursor: "pointer",
  fontSize: "12px",
  fontWeight: 600,
});


function SyncStatusPanel() {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const { data, isLoading, isFetching } = useSyncStatus({ page, limit: 10, filter, search });

  const companies = data?.companies || [];
  const pagination = data?.pagination || { page: 1, total: 0, totalPages: 1, limit: 10 };
  const counts = data?.counts || { all: 0, pending: 0, synced: 0 };

  const handleFilterChange = useCallback((f) => {
    setFilter(f);
    setPage(1);
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearch(e.target.value);
    setPage(1);
  }, []);

  const renderPageButtons = () => {
    const { totalPages } = pagination;
    if (totalPages <= 1) return null;
    const current = pagination.page;
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || Math.abs(i - current) <= 1) {
        pages.push(i);
      }
    }
    const result = [];
    for (let idx = 0; idx < pages.length; idx++) {
      if (idx > 0 && pages[idx] - pages[idx - 1] > 1) {
        result.push(
          <span key={`e-${idx}`} style={{ padding: "4px 6px", fontSize: "13px", color: "#9ca3af" }}>…</span>
        );
      }
      const p = pages[idx];
      result.push(
        <button key={p} style={pageBtnStyle(p === current)} onClick={() => setPage(p)}>{p}</button>
      );
    }
    return result;
  };

  return (
    <>
      <div style={{ display: "flex", alignItems: "center" }}>
        <Button
          color={counts.pending > 0 ? "warning" : "success"}
          size="sm"
          onClick={() => setOpen(true)}
        >
          Synced Status
          {/* {counts.pending > 0 && `(${counts.pending} pending)`} */}
        </Button>
      </div>

      <Modal isOpen={open} toggle={() => setOpen(false)} size="lg" style={{ maxWidth: "900px" }}>
        <ModalHeader toggle={() => setOpen(false)}>Company Sync Status & Progress</ModalHeader>
        <ModalBody>
          {/* Search + Filter row */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px", flexWrap: "wrap", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <input
                type="text"
                placeholder="Search company..."
                value={search}
                onChange={handleSearchChange}
                style={{
                  padding: "5px 10px",
                  border: "1px solid #d1d5db",
                  borderRadius: "4px",
                  fontSize: "13px",
                  width: "180px",
                }}
              />
              {["all", "pending", "synced"].map((f) => (
                <button key={f} onClick={() => handleFilterChange(f)} style={filterBtnStyle(filter === f)}>
                  {f === "all" ? `All (${counts.all})` : f === "pending" ? `Pending (${counts.pending})` : `Synced (${counts.synced})`}
                </button>
              ))}
            </div>
            {isFetching && <span style={{ fontSize: "12px", color: "#9ca3af" }}>Updating data...</span>}
          </div>

          {/* Table */}
          <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: "6px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, textAlign: "left", width: "40px" }}>#</th>
                  <th style={{ ...thStyle, textAlign: "left" }}>Company</th>
                  <th style={thStyle}>Material</th>
                  <th style={thStyle}>Labor</th>
                  <th style={thStyle}>Carting</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={5} style={{ ...tdStyle, textAlign: "center", padding: "24px", color: "#9ca3af" }}>Loading Data...</td>
                  </tr>
                ) : companies.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ ...tdStyle, textAlign: "center", padding: "24px", color: "#9ca3af" }}>
                      No companies match this filter.
                    </td>
                  </tr>
                ) : (
                  companies.map((c, i) => (
                    <tr key={c.companyId} style={{ background: i % 2 === 0 ? "#fff" : "#fafbfc" }}>
                      <td style={{ ...tdStyle, textAlign: "left", color: "#9ca3af" }}>
                        {(pagination.page - 1) * pagination.limit + i + 1}
                      </td>
                      <td style={{ ...tdStyle, textAlign: "left", fontWeight: 500 }}>{c.companyName}</td>
                      {["material", "labor", "carting"].map((type) => (
                        <td key={type} style={tdStyle}>
                          <span style={badgeStyle(c.syncs[type]?.isSynced)}>
                            {c.syncs[type]?.isSynced ? "Synced" : "Pending"}
                          </span>
                          <br />
                          <small style={{ color: "#9ca3af", fontSize: "11px" }}>
                            {formatDateTimeShort(c.syncs[type]?.lastSyncAt) || "Never"}
                          </small>
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "12px" }}>
              <span style={{ fontSize: "12px", color: "#6b7280" }}>
                Showing {(pagination.page - 1) * pagination.limit + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
              </span>
              <div style={{ display: "flex", gap: "4px" }}>
                <button
                  style={{ ...pageBtnStyle(false), opacity: pagination.page === 1 ? 0.4 : 1 }}
                  disabled={pagination.page === 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  ‹
                </button>
                {renderPageButtons()}
                <button
                  style={{ ...pageBtnStyle(false), opacity: pagination.page === pagination.totalPages ? 0.4 : 1 }}
                  disabled={pagination.page === pagination.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  ›
                </button>
              </div>
            </div>
          )}

          {/* Latest master update info */}
          {data?.latestMasterUpdate && (
            <div style={{ marginTop: "16px", padding: "12px", background: "#f3f4f6", borderRadius: "6px", fontSize: "11px", color: "#4b5563" }}>
              <div style={{ fontWeight: 700, marginBottom: "4px" }}>Last Published Date (By Category):</div>
              <div style={{ display: "flex", gap: "15px" }}>
                <span>Material: {formatDateTimeShort(data.latestMasterUpdate.material) || "Never"}</span>
                <span>Labor: {formatDateTimeShort(data.latestMasterUpdate.labor) || "Never"}</span>
                <span>Carting: {formatDateTimeShort(data.latestMasterUpdate.carting) || "Never"}</span>
              </div>
            </div>
          )}
        </ModalBody>
      </Modal>
    </>
  );
}

export default React.memo(SyncStatusPanel);
